import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MessageSquare, Ruler, PenTool, Factory, Truck } from 'lucide-react';

const steps = [
  {
    icon: MessageSquare,
    num: '01',
    title: 'Consultanță',
    desc: 'Discutăm nevoile tale, bugetul și preferințele de stil. Consultanța este gratuită și fără obligații.',
  },
  {
    icon: Ruler,
    num: '02',
    title: 'Măsurători',
    desc: 'Venim la fața locului pentru măsurători precise și evaluarea spațiului disponibil.',
  },
  {
    icon: PenTool,
    num: '03',
    title: 'Design',
    desc: 'Creăm proiectul detaliat cu materiale, culori și finisaje. Revizuiri nelimitate.',
  },
  {
    icon: Factory,
    num: '04',
    title: 'Producție',
    desc: 'Fabricăm mobilierul în atelierul nostru cu materiale premium și atenție la detalii.',
  },
  {
    icon: Truck,
    num: '05',
    title: 'Montaj',
    desc: 'Livrăm și montăm profesional. Verificăm fiecare detaliu pentru satisfacția ta completă.',
  },
];

function StepCard({ step, index, total }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const Icon = step.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.12 }}
      data-testid={`process-step-${index}`}
      className="relative flex flex-col items-center text-center"
    >
      {index < total - 1 && (
        <div className="hidden lg:block absolute top-10 left-[60%] w-[calc(100%-20px)] h-px border-t-2 border-dashed border-[#E3DACB] dark:border-[#3D3A37]" />
      )}

      <div className="relative z-10 w-20 h-20 rounded-2xl bg-[#A67B5B]/10 dark:bg-[#A67B5B]/20 flex items-center justify-center mb-5">
        <Icon size={28} className="text-[#A67B5B]" />
        <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#A67B5B] text-white text-xs font-bold flex items-center justify-center">
          {step.num}
        </span>
      </div>

      <h3 className="font-['Outfit'] text-lg font-medium text-[#2C2A29] dark:text-[#F0EDE8] mb-2">
        {step.title}
      </h3>
      <p className="text-sm leading-relaxed text-[#6B6561] dark:text-[#9B9590] max-w-[220px]">
        {step.desc}
      </p>
    </motion.div>
  );
}

export default function ProcessSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="proces" data-testid="process-section" className="py-20 lg:py-32 bg-[#F9F6F0] dark:bg-[#1A1918]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#A67B5B] mb-4">
            Cum lucrăm
          </p>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl tracking-tight font-medium text-[#2C2A29] dark:text-[#F0EDE8]">
            Procesul nostru în 5 pași
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-6">
          {steps.map((step, i) => (
            <StepCard key={step.num} step={step} index={i} total={steps.length} />
          ))}
        </div>
      </div>
    </section>
  );
}
